import React, { useMemo, useState } from 'react';

import { useTranslation } from '@crehana/i18n';
import { CuiOptionType, Dropdown } from '@crehana/ui';

import { useCheckoutContext } from 'Jsx/CheckoutV4/Context';
import { CheckoutTKeys } from 'Jsx/CheckoutV4/i18n/language';

import InputField, { ChangeState } from '../InputField';

import getStatesFor from './utils/getStates';

export interface CityAndStateState {
  state: string;
  city: string;
  isValid: boolean;
}

interface CityAndStateInputProps {
  onCityAndStateChange: ({ state, city, isValid }: CityAndStateState) => void;
}

const CityAndStateInput = ({
  onCityAndStateChange,
}: CityAndStateInputProps) => {
  const { t } = useTranslation<CheckoutTKeys>();

  const CITY_NAME_LABEL = t('CITY_NAME_LABEL');
  const STATE_NAME_LABEL = t('STATE_NAME_LABEL');
  const PROVINCE_NAME_LABEL = t('PROVINCE_NAME_LABEL');
  const DEPARTMENT_NAME_LABEL = t('DEPARTMENT_NAME_LABEL');
  const REQUIRED_PROVINCE_NAME = t('REQUIRED_PROVINCE_NAME');
  const REQUIRED_CITY_NAME = t('REQUIRED_CITY_NAME');
  const REQUIRED_DEPARTMENT_NAME = t('REQUIRED_DEPARTMENT_NAME');
  const REQUIRED_STATE_NAME = t('REQUIRED_STATE_NAME');

  const { countryPrefix } = useCheckoutContext();

  const cityTranslation = useMemo(() => {
    return {
      label: countryPrefix === 'pe' ? PROVINCE_NAME_LABEL : CITY_NAME_LABEL,
      required:
        countryPrefix === 'pe' ? REQUIRED_PROVINCE_NAME : REQUIRED_CITY_NAME,
    };
  }, [countryPrefix]);

  const stateTranslation = useMemo(() => {
    switch (countryPrefix) {
      case 'ar':
        return {
          label: PROVINCE_NAME_LABEL,
          required: REQUIRED_PROVINCE_NAME,
        };
      case 'pe':
      case 'co':
        return {
          label: DEPARTMENT_NAME_LABEL,
          required: REQUIRED_DEPARTMENT_NAME,
        };
      default:
        return {
          label: STATE_NAME_LABEL,
          required: REQUIRED_STATE_NAME,
        };
    }
  }, [countryPrefix]);

  const [city, setCity] = useState<ChangeState>();
  const [state, setState] = useState<ChangeState>();
  const statesList = useMemo<{ value: string; label: string }[] | null>(() => {
    return getStatesFor(countryPrefix);
  }, [countryPrefix]);
  const [dropdownSelection, setDropdownSelection] =
    useState<CuiOptionType<string> | undefined>();

  const cityValidation = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value.length > 3) {
      return {
        formattedValue: value,
        errorState: { error: false, message: '' },
      };
    }

    return {
      formattedValue: value,
      errorState: {
        error: true,
        message: cityTranslation.required,
      },
    };
  };

  const stateValidation = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value.length > 3) {
      return {
        formattedValue: value,
        errorState: { error: false, message: '' },
      };
    }

    return {
      formattedValue: value,
      errorState: {
        error: true,
        message: stateTranslation.required,
      },
    };
  };

  const onCityChange = (cityState: ChangeState) => {
    setCity(cityState);
    onCityAndStateChange({
      state: state?.value || '',
      city: cityState.value,
      isValid: cityState.isValid && (state?.isValid || false),
    });
  };

  const onStateChange = (stateChange: ChangeState) => {
    setState(stateChange);
    onCityAndStateChange({
      city: city?.value || '',
      state: stateChange.value,
      isValid: stateChange.isValid && (city?.isValid || false),
    });
  };

  const handleStateDropdownChange = (selected: CuiOptionType | undefined) => {
    const parsedSelected = selected?.label ?? '';
    const isStateValid = parsedSelected.length > 3;

    setDropdownSelection(selected);
    setState({ value: parsedSelected, isValid: isStateValid });
    onCityAndStateChange({
      city: city?.value || '',
      state: parsedSelected,
      isValid: isStateValid && (city?.isValid || false),
    });
  };

  return (
    <>
      <div className="mb-24">
        {statesList ? (
          <Dropdown
            options={statesList}
            selected={dropdownSelection}
            onSelected={handleStateDropdownChange}
            required
            full
            size={4}
            data-testid="state-field"
            label={stateTranslation.label}
          />
        ) : (
          <InputField
            id="state"
            name="state"
            testId="state-field"
            label={stateTranslation.label}
            maxLength={50}
            onChange={onStateChange}
            validation={stateValidation}
          />
        )}
        <InputField
          id="city"
          name="city"
          testId="city-field"
          className="mt-24"
          label={cityTranslation.label}
          maxLength={50}
          onChange={onCityChange}
          validation={cityValidation}
        />
      </div>
    </>
  );
};

export default CityAndStateInput;
